/**
 * Detail-view helpers layered on top of the pure monthly resolver
 * (`cashflow-metrics.ts`): overlay manually-logged expenses onto each month and
 * roll the series up into year totals. Kept pure + tested.
 */
import type { MonthlyResult, YearMonth } from "./cashflow-metrics";
import { monthKey } from "./cashflow-metrics";
import type { PropertyExpense } from "@/hooks/use-property-expenses";

/** Add-expense categories → the `manual_transactions.category` (Schedule E key). */
export const EXPENSE_CATEGORIES = [
  { value: "repairs", label: "Maintenance & repairs" },
  { value: "taxes", label: "Property taxes" },
  { value: "insurance", label: "Insurance" },
  { value: "management_fees", label: "Property management" },
  { value: "utilities", label: "Utilities" },
  { value: "legal_professional", label: "Legal & professional" },
  { value: "advertising", label: "Advertising" },
  { value: "other", label: "Other" },
] as const;

export interface MonthWithLogged extends MonthlyResult {
  /** Manual expenses logged in this month. */
  logged: PropertyExpense[];
  loggedTotal: number;
  /** Net after subtracting logged expenses (and adding logged income). */
  netWithLogged: number;
}

/** Bucket key for a property expense ("YYYY-MM"). */
function expenseYm(dateStr: string): string {
  return dateStr.slice(0, 7);
}

/**
 * Attach logged expenses to their month and recompute net. Logged expenses
 * reduce net; logged income (type === "income") increases it.
 */
export function overlayLoggedExpenses(
  months: MonthlyResult[],
  expenses: PropertyExpense[],
): MonthWithLogged[] {
  const byYm = new Map<string, PropertyExpense[]>();
  for (const e of expenses) {
    const ym = expenseYm(e.transaction_date);
    const list = byYm.get(ym);
    if (list) list.push(e);
    else byYm.set(ym, [e]);
  }

  return months.map((m) => {
    const ym = `${m.year}-${String(m.month).padStart(2, "0")}`;
    const logged = byYm.get(ym) ?? [];
    const signedTotal = logged.reduce(
      (s, e) => s + (e.type === "income" ? Number(e.amount) : -Number(e.amount)),
      0,
    );
    const loggedTotal = logged.reduce(
      (s, e) => (e.type === "expense" ? s + Number(e.amount) : s),
      0,
    );
    return {
      ...m,
      logged,
      loggedTotal,
      netWithLogged: m.breakdown.net + signedTotal,
    };
  });
}

/**
 * Strict-actual rent overlay for the trailing-month series (`buildPropertyMonths`
 * output). A month's gross income becomes the rent actually collected that month
 * (`rent_payments` summed by `paid_date`); EGI and net are recomputed with opex +
 * debt held fixed. Months with no payment show $0 income. Mirrors
 * `overlayRentPayments` but for `MonthlyResult` (no rehab-spend line).
 */
export function overlayActualRentMonthly(
  months: MonthlyResult[],
  rentByMonth: Map<string, number>,
): MonthlyResult[] {
  return months.map((m) => {
    const ym = `${m.year}-${String(m.month).padStart(2, "0")}`;
    const gross = rentByMonth.get(ym) ?? 0;
    const egi = gross + m.breakdown.income.other;
    const net = egi - m.breakdown.opex.total - m.breakdown.debt;
    return {
      ...m,
      breakdown: {
        ...m.breakdown,
        income: { ...m.breakdown.income, gross, egi },
        net,
      },
    };
  });
}

export interface YearTotals {
  income: number;
  operatingAndDebt: number;
  logged: number;
  net: number;
}

/** Year-to-date roll-up across the resolved months. */
export function yearTotals(months: MonthWithLogged[]): YearTotals {
  let income = 0;
  let operatingAndDebt = 0;
  let logged = 0;
  let net = 0;
  for (const m of months) {
    income += m.breakdown.income.egi;
    operatingAndDebt += m.breakdown.opex.total + m.breakdown.debt;
    logged += m.loggedTotal;
    net += m.netWithLogged;
  }
  return { income, operatingAndDebt, logged, net };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Full-year (detail-view) overlay + totals
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Strict-actual rent overlay: a month's gross income becomes the rent actually
 * collected that month (sum of `rent_payments` by `paid_date`), recomputing EGI
 * and net. Months with no recorded payment show $0 income (no projection).
 */
export function overlayRentPayments(
  months: YearMonth[],
  rentByMonth: Map<string, number>,
): YearMonth[] {
  return months.map((m) => {
    if (!m.breakdown) return m;
    const ym = `${m.year}-${String(m.month).padStart(2, "0")}`;
    const gross = rentByMonth.get(ym) ?? 0;
    const egi = gross + m.breakdown.income.other;
    const net = egi - m.breakdown.opex.total - m.breakdown.debt;
    return {
      ...m,
      breakdown: { ...m.breakdown, income: { ...m.breakdown.income, gross, egi }, net },
      net: net - m.rehabSpend,
    };
  });
}

export interface YearMonthWithLogged extends YearMonth {
  logged: PropertyExpense[];
  loggedTotal: number;
  /** net (incl. rehab) minus logged expenses, plus logged income. */
  netWithLogged: number;
}

/** Attach logged expenses to each month of the year and recompute net. */
export function overlayYearExpenses(
  months: YearMonth[],
  expenses: PropertyExpense[],
): YearMonthWithLogged[] {
  const byYm = new Map<string, PropertyExpense[]>();
  for (const e of expenses) {
    const ym = expenseYm(e.transaction_date);
    const list = byYm.get(ym);
    if (list) list.push(e);
    else byYm.set(ym, [e]);
  }
  return months.map((m) => {
    const ym = `${m.year}-${String(m.month).padStart(2, "0")}`;
    const logged =
      m.phase === "pre" || m.phase === "future" || m.phase === "sold"
        ? []
        : byYm.get(ym) ?? [];
    const signed = logged.reduce(
      (s, e) => s + (e.type === "income" ? Number(e.amount) : -Number(e.amount)),
      0,
    );
    const loggedTotal = logged.reduce(
      (s, e) => (e.type === "expense" ? s + Number(e.amount) : s),
      0,
    );
    return { ...m, logged, loggedTotal, netWithLogged: m.net + signed };
  });
}

export interface YearSummary {
  income: number;
  operatingAndDebt: number;
  rehab: number;
  logged: number;
  net: number;
  /** Net excluding one-time rehab capital. */
  netExRehab: number;
}

/** Year-to-date roll-up across months that have figures (past + current). */
export function summarizeYear(months: YearMonthWithLogged[]): YearSummary {
  let income = 0;
  let operatingAndDebt = 0;
  let rehab = 0;
  let logged = 0;
  let net = 0;
  for (const m of months) {
    // Income / opex / debt only exist on months with a resolved breakdown, but
    // rehab spend, logged expenses and net can land on a breakdown-less month
    // (e.g. rehab spend distributed into a month that was never snapshotted).
    // Pre / future months carry zeros for all of these, so they contribute
    // nothing.
    if (m.breakdown) {
      income += m.breakdown.income.egi;
      operatingAndDebt += m.breakdown.opex.total + m.breakdown.debt;
    }
    rehab += m.rehabSpend;
    logged += m.loggedTotal;
    net += m.netWithLogged;
  }
  return { income, operatingAndDebt, rehab, logged, net, netExRehab: net + rehab };
}

/** Re-export so callers don't need a second import for keying. */
export { monthKey };
